import { useEffect, useState } from 'react';

import {
  selectActiveApplication,
  selectActiveUserId,
  selectApplications
} from 'features/applications/applicationsSelectors';
import applicationsService from 'features/applications/applicationsService';
import {
  addApplication,
  updateApplication
} from 'features/applications/applicationsSlice';
import { closeSidebar } from 'features/sidebar/sidebarSlice';

import useErrorCatch from 'hooks/useErrorCatch';
import { useAppDispatch, useAppSelector } from 'hooks/useTypedRedux';

import { useTranslation } from 'react-i18next';
import { Application } from 'types/applications';

const useGetApplication = (): {
  application: Application | undefined;
  isLoading: boolean;
  fetchApplicationData: (applicationId: string) => Promise<void>;
} => {
  const { t } = useTranslation()

  const dispatch = useAppDispatch();
  const catchError = useErrorCatch();

  const applicationId = useAppSelector(selectActiveUserId);
  const application = useAppSelector(selectActiveApplication);
  const applications = useAppSelector(selectApplications);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchApplicationData = async (applicationId: string): Promise<void> => {
    try {
      setIsLoading(true);
      const res = await applicationsService.getApplicationById(applicationId);

      if (!applications.find((application) => application.id === res.id)) {
        dispatch(addApplication(res));
      } else {
        dispatch(updateApplication(res));
      }
    } catch (error) {
      catchError({
        error,
        default: {
          message: t('error.message.operationFailed'),
          description: t('error.message.fetchApplication')
        }
      });
      dispatch(closeSidebar());
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!applicationId) {
      return;
    }
    fetchApplicationData(applicationId);
  }, [applicationId]);

  return { application, isLoading, fetchApplicationData };
};

export default useGetApplication;
